import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

/**
 * GLB 파일에서 썸네일 이미지를 생성합니다
 * @param {string} url - GLB 파일 URL
 * @param {number} size - 썸네일 크기 (기본값: 200)
 * @returns {Promise<string>} - Base64 인코딩된 이미지 데이터 URL
 */
export async function generateGLBThumbnail(url, size = 200) {
  return new Promise((resolve, reject) => {
    // 오프스크린 캔버스 생성
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    // WebGL 렌더러 생성
    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true
    });
    renderer.setSize(size, size);
    renderer.setClearColor(0xf5f5f5, 1); // 밝은 회색 배경

    // 씬과 카메라 설정
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 1000);
    camera.position.set(0, 0, 2.2); // 3에서 2.2로 더 가까이

    // 조명 설정
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight1.position.set(5, 5, 5);
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight2.position.set(-5, -5, -5);
    scene.add(directionalLight2);

    // GLB 모델 로드
    const loader = new GLTFLoader();

    loader.load(
      url,
      (gltf) => {
        try {
          const model = gltf.scene;

          // 모델 크기와 중심 계산
          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());

          // 모델을 화면에 맞게 스케일링 (여유 공간 확보)
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = 1.8 / maxDim; // 2에서 1.8로 줄여서 여유 공간 확보
          model.scale.setScalar(scale);

          // 모델을 원점으로 이동 (중앙 정렬)
          model.position.x = -center.x * scale;
          model.position.y = -center.y * scale;
          model.position.z = -center.z * scale;

          // 모델을 약간 회전시켜 입체감 부여
          model.rotation.y = Math.PI / 6;
          model.rotation.x = -Math.PI / 12;

          scene.add(model);

          // 렌더링
          renderer.render(scene, camera);

          // 캔버스를 이미지로 변환
          const imageData = canvas.toDataURL('image/png');

          // 리소스 완전히 정리
          model.traverse((child) => {
            if (child.isMesh) {
              if (child.geometry) child.geometry.dispose();
              if (child.material) {
                if (Array.isArray(child.material)) {
                  child.material.forEach(material => material.dispose());
                } else {
                  child.material.dispose();
                }
              }
            }
          });

          scene.remove(model);
          scene.clear();
          renderer.dispose();
          renderer.forceContextLoss();

          resolve(imageData);
        } catch (error) {
          reject(error);
        }
      },
      undefined,
      (error) => {
        console.error('GLB 로드 실패:', error);
        reject(error);
      }
    );
  });
}

/**
 * 여러 GLB 파일의 썸네일을 배치로 생성합니다
 * @param {Array<{url: string, key: string}>} files - GLB 파일 정보 배열
 * @param {number} size - 썸네일 크기
 * @returns {Promise<Map<string, string>>} - 파일 키와 썸네일 데이터의 맵
 */
export async function generateGLBThumbnailBatch(files, size = 200) {
  const thumbnails = new Map();

  // 동시에 처리할 파일 수 제한 (성능 최적화)
  const BATCH_SIZE = 3;

  for (let i = 0; i < files.length; i += BATCH_SIZE) {
    const batch = files.slice(i, i + BATCH_SIZE);

    const promises = batch.map(async (file) => {
      try {
        const thumbnail = await generateGLBThumbnail(file.url, size);
        return { key: file.key, thumbnail };
      } catch (error) {
        console.error(`썸네일 생성 실패: ${file.key}`, error);
        return { key: file.key, thumbnail: null };
      }
    });

    const results = await Promise.all(promises);
    results.forEach(({ key, thumbnail }) => {
      if (thumbnail) {
        thumbnails.set(key, thumbnail);
      }
    });
  }

  return thumbnails;
}
